import { Injectable } from '@angular/core';

export type ReactiveDropdownCacheConfig = {
    readonly namespace: string;
    readonly name: string;
    readonly ttlMs: number;
    readonly endpoint?: unknown;
    readonly params?: Record<string, unknown>;
    readonly userScope?: string | number | null;
    readonly clientScope?: string | number | null;
    readonly appName?: string | null;
    readonly cacheEmptyResults?: boolean;
};

type ReactiveDropdownCacheEntry = {
    readonly cachedAt: number;
    readonly values: unknown[];
    readonly filtered: unknown[];
};

type MutableDropdownListSnapshot = {
    values: unknown[];
    filtered: unknown[];
};

/** Generic in-memory cache for progressive dropdown data. */
@Injectable({ providedIn: 'root' })
export class ReactiveDropdownCacheService {
    private readonly cache = new Map<string, ReactiveDropdownCacheEntry>();

    public restore(config: ReactiveDropdownCacheConfig, list: unknown): boolean {
        const cacheKey = this.buildCacheKey(config);
        const cached = this.cache.get(cacheKey);

        if (!cached) {
            return false;
        }

        if (Date.now() - cached.cachedAt > config.ttlMs) {
            this.cache.delete(cacheKey);
            return false;
        }

        if (config.cacheEmptyResults !== true && !this.hasCacheableOptions(cached)) {
            this.cache.delete(cacheKey);
            return false;
        }

        const target = list as MutableDropdownListSnapshot;
        target.values = [...cached.values];
        target.filtered = [...cached.filtered];

        return true;
    }

    public save(config: ReactiveDropdownCacheConfig, list: unknown): void {
        const cacheKey = this.buildCacheKey(config);
        const source = list as MutableDropdownListSnapshot;
        const nextEntry = {
            cachedAt: Date.now(),
            values: [...(source.values ?? [])],
            filtered: [...(source.filtered ?? [])],
        };

        if (config.cacheEmptyResults !== true && !this.hasCacheableOptions(nextEntry)) {
            this.cache.delete(cacheKey);
            return;
        }

        this.cache.set(cacheKey, nextEntry);
    }

    public clear(config: ReactiveDropdownCacheConfig): void {
        this.cache.delete(this.buildCacheKey(config));
    }

    private hasCacheableOptions(entry: Pick<ReactiveDropdownCacheEntry, 'values' | 'filtered'>): boolean {
        return [...(entry.values ?? []), ...(entry.filtered ?? [])].some((option) => this.isRealOption(option));
    }

    private isRealOption(option: unknown): boolean {
        if (option === null || option === undefined) {
            return false;
        }

        if (typeof option !== 'object') {
            return String(option).trim() !== '';
        }

        const record = option as Record<string, unknown>;
        const label = String(record['label'] ?? '').trim();
        const value = record['value'];

        return label !== '' && label !== '...' && value !== null && value !== undefined;
    }

    private buildCacheKey(config: ReactiveDropdownCacheConfig): string {
        return [
            config.namespace,
            config.name,
            this.stringifyEndpoint(config.endpoint ?? config.name),
            String(config.userScope ?? 'all-users'),
            String(config.clientScope ?? 'all-clients'),
            String(config.appName ?? 'all-apps'),
            this.stringifyParams(config.params ?? {}),
        ].join(':');
    }

    private stringifyEndpoint(endpoint: unknown): string {
        if (typeof endpoint === 'string') {
            return endpoint;
        }

        if (endpoint && typeof endpoint === 'object') {
            const requestData = (endpoint as { getRequestData?: () => unknown }).getRequestData?.();

            if (requestData && typeof requestData === 'object') {
                const record = requestData as Record<string, unknown>;

                return String(record['endpoint'] ?? record['url'] ?? this.stringifyParams(record));
            }
        }

        return String(endpoint ?? 'unknown-endpoint');
    }

    private stringifyParams(params: Record<string, unknown>): string {
        return JSON.stringify(this.sortRecord(params));
    }

    private sortRecord(params: Record<string, unknown>): Record<string, unknown> {
        return Object.keys(params)
            .sort()
            .reduce<Record<string, unknown>>((sorted, key) => {
                sorted[key] = params[key];

                return sorted;
            }, {});
    }
}
